import Autocomplete, {
   AutocompleteChangeReason,
   AutocompleteProps,
   AutocompleteRenderInputParams,
   AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useCallback, useMemo } from 'react'
import { notNull } from '@gap/utils/utils'
import TextFieldCustom from '../text-field/TextFieldCustom'
import { AutocompleteOption } from './shared/AutocompleteOption'
import ListItem from '@mui/material/ListItem'
import DoneIcon from '@mui/icons-material/Done';
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'

interface AutocompleteSingleNonClearableProps
   extends Omit<
      AutocompleteProps<AutocompleteOption, undefined, true, undefined>,
      | 'multiple'
      | 'freeSolo'
      | 'onChange'
      | 'disableClearable'
      | 'value'
      | 'renderInput'
      | 'getOptionLabel'
   > {
   // omit onChange and value and define our own versions below
   onChange?: (value: any) => any
   value?: string
   label?: string
   helperText?: string
   error?: boolean
   variant?: React.ComponentProps<typeof TextField>['variant']
   autoFocus?: boolean
}

// This exports a custom AutocompleteSingle component which wraps the MUI Autocomplete
export default observer(function AutocompleteSingleNonClearable(props: AutocompleteSingleNonClearableProps) {
   // This just separates out the onChange function and the value property explicitly
   // while spreading the rest of props into a componentProps variable
   const { onChange, value, label, error, helperText, variant, autoFocus, autoHighlight, autoSelect, ...componentProps } = props

   const componentOnChange = useCallback(
      (event: React.SyntheticEvent, value: any, reason: AutocompleteChangeReason) => {
         // Check to see if an onChange event handler exists, then calls it with the
         // selected value id or undefined
         if (onChange) {
            onChange(value?.id || value)
         }
      },
      [onChange]
   )

   const componentValue = useMemo(() => {
      // value needs an initial value other than undefined otherwise MUI assumes that automcomplete is an uncontrolled component and give this error:
      // A component is changing the uncontrolled value state of Autocomplete to be controlled.
      // ref: https://stackoverflow.com/questions/70587660/autocomplete-gives-red-warning-when-value-is-set-to-the-usestate-that-it-modifie/70587700
      return notNull(props.options.find(i => i.id === value))
   }, [value, props.options])

   const renderTextFieldInput = useCallback((params: AutocompleteRenderInputParams) => {
      return <TextFieldCustom
         {...params}
         autoFocus={autoFocus}
         helperText={helperText}
         error={error}
         label={label}
         variant={variant}
      />
   }, [autoFocus, error, helperText, label, variant])

   const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, { label }: AutocompleteOption, { selected }: AutocompleteRenderOptionState) => {
      return <ListItem {...props}>
         <ListItemText primary={label} />
         {selected && <ListItemIcon><DoneIcon /></ListItemIcon>}
      </ListItem>
   }, [])

   const getOptionLabel = (option: AutocompleteOption) => option.label || ''

   return (
      <Autocomplete<AutocompleteOption, undefined, true, undefined>
         {...componentProps}
         disableClearable
         autoHighlight={typeof autoHighlight === "undefined" ? true : autoHighlight}
         onChange={componentOnChange}
         value={componentValue}
         getOptionLabel={getOptionLabel}
         autoSelect={typeof autoSelect === "undefined" ? true : autoSelect}
         renderInput={renderTextFieldInput}
         renderOption={renderOption}
      />
   )
})
